import * as THREE from 'three';
import { ColoredObjectSchema } from './ColoredObject';
import { GlintEffect } from './glint';
import { SelectableObject } from './selectableObject';
import { stringSchema } from '@/data/common';
import z from 'zod';

export const InverseQuarterCylinderSchema = ColoredObjectSchema.extend({
  name: stringSchema.default('InverseQuarterCylinder'),
});

export class InverseQuarterCylinder extends SelectableObject<
  typeof InverseQuarterCylinderSchema
> {
  static override schema = InverseQuarterCylinderSchema;

  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshStandardMaterial;
  private readonly glintEffect: GlintEffect;

  constructor(options: z.input<typeof InverseQuarterCylinderSchema> = {}) {
    super(options);

    this.material = new THREE.MeshStandardMaterial({
      color: this.data.color,
      emissive: 0x000000,
      roughness: 0.35,
      metalness: 0.05,
    });
    const shape = new THREE.Shape();

    // Define the inverse-quarter-circle arc
    const radius = 0.5;
    shape.moveTo(radius, radius);
    shape.lineTo(radius, 0);
    shape.absarc(0, 0, radius, 0, Math.PI / 2, false);
    shape.lineTo(radius, radius);

    const extrudeSettings = {
      depth: 1,
      bevelEnabled: false,
    };
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    geometry.rotateX(-Math.PI / 2);
    geometry.rotateY(Math.PI);
    geometry.scale(2, 1, 2);
    geometry.translate(0.5, -0.5, -0.5);

    this.mesh = new THREE.Mesh(geometry, this.material);

    this.glintEffect = new GlintEffect(this.material);
  }

  protected renderObject(parent: THREE.Object3D) {
    parent.add(this.mesh);
  }

  override getPickTargets() {
    return [this.mesh];
  }

  override getAabb() {
    return new THREE.Box3().setFromCenterAndSize(
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(1, 1, 1),
    );
  }

  override update(delta: number): void {
    this.glintEffect.update(delta);
  }

  override dispose() {
    super.dispose();
    this.glintEffect.dispose();
    this.mesh.geometry.dispose();
    this.material.dispose();
  }

  protected override onSelectionChanged(selected: boolean): void {
    this.glintEffect.setSelected(selected);
  }
}
